import { z } from 'zod';

import type { PK } from '../database.schema';
import { baseDatabaseSchema, userSchema } from '../database.schema';

/**
 * Supported workflow types.
 *
 * - `LENS_BY_LENS` — one Bedrock call per `lens:*` prompt pack (open-ended
 *   review per angle). The original workflow; kept unchanged so eval rounds
 *   against it stay comparable.
 *
 * `SINGLE_PASS` and `SECTION_BY_SECTION` were removed (never used in a real
 * config), and `LENS_AND_AUDIT` (lenses plus schema-forced verdicts per
 * `checklist:*` check) was deprecated and removed in Sept 2026. Like
 * `EvalModelIdSchema`, this enum gates new writes
 * only — stored rows are read without re-parsing, so a config holding a
 * removed type keeps loading (but can no longer run — see `selectWorkflow`),
 * and old runs keep their `check_results` step rows.
 */
export const EvalWorkflowTypeSchema = z.enum(['LENS_BY_LENS']);

/**
 * Supported Bedrock model ids for PBQA eval runs.
 *
 * All IDs are EU cross-region inference profiles since our Lambdas run in
 * eu-west-1. All models support the Bedrock Converse features we rely on —
 * `toolConfig` for structured findings output, `cachePoint` for prompt
 * caching of the static system + guidelines prefix, and adaptive thinking
 * (see `bedrockClient.ts`). Opus 4.8 stays the default until an Opus 5
 * eval round justifies switching; Opus 5 is the newest generation and 4.7
 * is kept as a pin-back option.
 *
 * This enum gates new writes only (create/update config payloads) — stored
 * rows are read without re-parsing, so configs holding a removed id (e.g.
 * the old Sonnet default) keep running until their model is next edited.
 */
export const EvalModelIdSchema = z.enum([
  'eu.anthropic.claude-opus-4-8',
  'eu.anthropic.claude-opus-5',
  'eu.anthropic.claude-opus-4-7',
]);

export const DEFAULT_EVAL_MODEL_ID: EvalModelId = 'eu.anthropic.claude-opus-4-8';

/**
 * One check inside a `checklist:*` prompt pack. The `id` must be stable
 * across edits — runs record verdicts against it, so a changed id severs the
 * link between a check and its historical pass/fail record.
 */
export const ChecklistCheckSchema = z
  .object({
    id: z.string().min(1),
    text: z.string().min(1),
  })
  .strict();

export const PromptPackSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    description: z.string().optional(),
    /**
     * Semantic role for the pack, e.g. 'lens:consistency' (open-ended review
     * angle). Valid roles per workflow are defined by the orchestration code.
     * `checklist:*` roles belonged to the removed LENS_AND_AUDIT workflow;
     * stored packs may still carry them, and the planner ignores them.
     */
    role: z.string().min(1),
    content: z.string(),
    /**
     * Structured checks of a legacy `checklist:*` pack (LENS_AND_AUDIT,
     * removed Sept 2026). Kept so stored configs still parse; nothing reads
     * them at run time.
     */
    checks: z.array(ChecklistCheckSchema).optional(),
  })
  .strict();

export const GuidelinePackSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    description: z.string().optional(),
    source_document: z.string().optional(),
    content_markdown: z.string(),
  })
  .strict();

/**
 * Mutable content of a draft row.
 *
 * Inlined with the draft DDB row (pk: eval_configs#<configId>, sk: draft).
 */
export const EvalConfigDraftContentSchema = z
  .object({
    workflow_type: EvalWorkflowTypeSchema,
    prompt_packs: z.array(PromptPackSchema),
    guideline_packs: z.array(GuidelinePackSchema),
  })
  .strict();

/**
 * Immutable snapshot content of a published version row.
 *
 * Inlined with the version DDB row (pk: eval_configs#<configId>, sk: version#<N>).
 */
export const EvalConfigVersionContentSchema = EvalConfigDraftContentSchema.extend({
  version_number: z.number().int().positive(),
  published_at: z.string(),
  published_by: userSchema.partial({ tenant_id: true }),
  change_note: z.string().optional(),
});

/**
 * Root metadata record (pk: eval_configs, sk: <configId>).
 */
export const EvalConfigRootSchema = z
  .object({
    name: z.string().min(1),
    description: z.string().optional(),
    workflow_type: EvalWorkflowTypeSchema,
    /**
     * Bedrock model to use when running this config. Stored on the root (not
     * on individual draft/version snapshots) so SMEs can bump the model
     * without publishing a new version. Past runs keep their own `model_id`
     * on the run record for reproducibility.
     */
    model_id: EvalModelIdSchema,
    current_version_number: z.number().int().positive().optional(),
    has_unpublished_draft_changes: z.boolean(),
    archived: z.boolean().default(false),
    /**
     * The one config brokers are allowed to see runs of (champion), as
     * opposed to the challenger configs internal staff keep re-running on
     * live contracts. At most one config per tenant carries this — the
     * SetChampionEvalConfig operation clears it from the others in the same
     * transaction. Absent on every config means brokers see nothing.
     */
    is_champion: z.boolean().optional(),
  })
  .strict();

export const DbEvalConfigRootSchema = EvalConfigRootSchema.merge(baseDatabaseSchema);
export const DbEvalConfigDraftSchema = EvalConfigDraftContentSchema.merge(baseDatabaseSchema);
export const DbEvalConfigVersionSchema = EvalConfigVersionContentSchema.merge(baseDatabaseSchema);

export type EvalWorkflowType = z.infer<typeof EvalWorkflowTypeSchema>;
export type EvalModelId = z.infer<typeof EvalModelIdSchema>;
export type ChecklistCheck = z.infer<typeof ChecklistCheckSchema>;
export type PromptPack = z.infer<typeof PromptPackSchema>;
export type GuidelinePack = z.infer<typeof GuidelinePackSchema>;
export type EvalConfigDraftContent = z.infer<typeof EvalConfigDraftContentSchema>;
export type EvalConfigVersionContent = z.infer<typeof EvalConfigVersionContentSchema>;
export type EvalConfigRoot = z.infer<typeof EvalConfigRootSchema>;

export type DbEvalConfigRoot = z.infer<typeof DbEvalConfigRootSchema> & PK;
export type DbEvalConfigDraft = z.infer<typeof DbEvalConfigDraftSchema> & PK;
export type DbEvalConfigVersion = z.infer<typeof DbEvalConfigVersionSchema> & PK;
